/**
 * The API contract. Imported by both `server/` and `src/` so a route and the screen that
 * renders it cannot drift apart.
 *
 * Money is in the player's round currency. Odds are decimal, and the odds a player sees
 * are bookie prices (shared/odds.ts). P&L carries its own sign — never re-derive a sign
 * from a colour.
 */

import type { ProfileDetails } from './waitlist.ts'

export type Mode = 'paper' | 'live'
export type TradingState = 'trading' | 'halted' | 'flattening' | 'stopped'
export type Freshness = 'live' | 'stale' | 'down'
export type Side = 'BACK' | 'LAY'
export type DecisionOutcome = 'PLACED' | 'VETOED' | 'SKIPPED' | 'FAILED'
export type BreachAction = 'REJECT' | 'HALT' | 'FLATTEN_AND_HALT'

/** The global chrome. Every core page is wrapped in this. */
export interface AppContext {
  mode: Mode
  state: TradingState
  stateCause: string | null
  freshness: Freshness
  freshnessLabel: string
  alerts: number
  engineRunning: boolean
  dataTier: 'delayed' | 'live'
  /** The player's current bankroll round, for the balance in the top bar. */
  round: { number: number; currency: Currency; balance: number; available: number; status: RoundStatus }
  asOf: string
}

export interface OutcomeRow {
  selectionId: number
  runner: string
  pnl: number
}

export interface PositionRow {
  marketId: string
  event: string
  marketType: string
  competition: string | null
  strategy: string
  startTime: string | null
  status: string | null
  inPlay: boolean
  suspended: boolean
  minute: number | null
  score: string | null
  liability: number
  unrealised: number
  outcomes: OutcomeRow[]
  /** Runners we hold exposure on in this market. "4 positions across 3 markets". */
  selections: number
  matchedOrders: number
  workingOrders: number
  unmatchedSize: number
  settling: boolean
}

export interface PositionsPayload {
  positions: PositionRow[]
  /** Open accumulators. Each stands to lose its stake, which counts in `totals.liability`. */
  accumulators: PnlBet[]
  totals: {
    /** Singles' worst case per market plus every open accumulator's stake. */
    liability: number
    liabilityLimit: number | null
    liabilityPct: number | null
    unrealised: number
    up: number
    down: number
    openPositions: number
    openMarkets: number
    openAccumulators: number
    workingOrders: number
    unmatchedSize: number
    matchedFills: number
    worstSingleOutcome: { pnl: number; label: string } | null
  }
  orphan: OrphanExposure | null
}

export interface OrphanExposure {
  marketId: string
  event: string
  marketType: string
  runner: string
  side: Side
  price: number
  size: number
  liability: number
  firstSeen: string | null
}

export interface TimelineEntry {
  id: number
  ts: string
  strategy: string
  text: string
  outcome: DecisionOutcome
  detail: string | null
}

export interface SafetyPanel {
  stream: { ok: boolean; label: string; detail: string }
  heartbeat: { ok: boolean; label: string; detail: string }
  reconciliation: { ok: boolean; label: string; detail: string }
  betfair: { ok: boolean; label: string; detail: string }
}

export interface StrategySummary {
  key: string
  name: string
  marketType: string
  phase: 'pre-match' | 'in-play'
  enabled: boolean
  status: 'ok' | 'throttled' | 'out_of_window' | 'disabled'
  statusReason: string | null
  decisionsToday: number
  vetoedToday: number
  lastDecisionAt: string | null
}

export interface AlertRow {
  id: number
  ts: string
  severity: 'info' | 'warn' | 'neg'
  title: string
  detail: string | null
  marketId: string | null
  read: boolean
}

export interface HomePayload {
  pnl: { total: number; realised: number; unrealised: number }
  liability: { total: number; limit: number | null; pct: number | null }
  openPositions: { count: number; markets: number; workingOrders: number; accumulators: number }
  engine: { running: boolean; label: string; strategiesLive: number; lastDecisionAgoS: number | null }
  safety: SafetyPanel
  positions: PositionRow[]
  timeline: TimelineEntry[]
  strategies: StrategySummary[]
  alerts: AlertRow[]
  /** The three biggest live price moves across every market. */
  signals: MarketSignal[]
  attention: { tone: 'degraded' | 'incident'; title: string; text: string; href?: string; hrefLabel?: string }[]
}

export interface LadderLevel {
  price: number
  size: number
}

export interface RunnerView {
  selectionId: number
  name: string
  /** The bookie price on offer; null when there is none. */
  price: number | null
}

/** One market: its selections and prices, and the player's bets on it. */
export interface MarketDetailPayload {
  marketId: string
  eventId: string | null
  event: string
  marketType: string
  competition: string | null
  startTime: string | null
  /** The game has kicked off: the market takes no bets. No in-play betting. */
  closed: boolean
  suspended: boolean
  score: string | null
  runners: RunnerView[]
  /** The player's bets on this market — singles, and accumulators with a leg on it. */
  bets: PnlBet[]
  signals: MarketSignal[]
}

/**
 * One row of the markets list. Deliberately not a cut-down `MarketDetailPayload`: the
 * list answers "which market should I look at", so it carries the market's own facts plus
 * the two things that make a row worth opening — whether a strategy is working it, and
 * whether we already hold exposure there.
 */
/**
 * A price that moved a long way in a short time, still worth knowing about.
 *
 * `steam` is a price that shortened (money came for the runner); `drift` one that
 * lengthened. `pct` and `ticks` are signed by price, so steam is negative. Signals expire
 * rather than being dismissed: the list is whatever is live now.
 */
export interface MarketSignal {
  marketId: string
  /** The game, so a signal can open it with every market; null for engine-only markets. */
  eventId: string | null
  event: string
  marketType: string
  competition: string | null
  startTime: string | null
  inPlay: boolean
  selectionId: number
  runner: string
  kind: 'steam' | 'drift'
  fromPrice: number
  toPrice: number
  ticks: number
  pct: number
  windowS: number
  detectedAt: string
  expiresAt: string
}

/**
 * An upcoming game in a league we price, from Betfair's event list. `marketId` and `prices`
 * are set once the odds collector has priced it — the same event id joins the two — and
 * `signals` counts the live moves on any of its markets.
 */
export interface FixtureRow {
  eventId: string
  name: string
  home: string
  away: string
  competition: string
  kickoff: string
  marketCount: number | null
  marketId: string | null
  /** The match odds as bookie prices, from the latest sweep. */
  prices: { home: number | null; draw: number | null; away: number | null } | null
  signals: number
}

export interface FixturesPayload {
  fixtures: FixtureRow[]
  /** When the fixtures job last wrote a game; null if it never has. */
  updatedAt: string | null
}

/** One runner on the game page, with its bookie price. */
export interface GameRunner {
  selectionId: number
  name: string
  /** Null when there is no price on offer. */
  price: number | null
}

export interface GameMarket {
  marketId: string
  name: string
  marketType: string
  totalMatched: number | null
  suspended: boolean
  runners: GameRunner[]
  /** Live moves on this market. */
  signals: number
}

/** A game and every market priced on it, grouped as the page shows them. */
export interface GamePayload {
  eventId: string
  event: string
  home: string
  away: string
  competition: string | null
  kickoff: string | null
  /** The game has kicked off: its markets are closed and take no bets. No in-play betting. */
  closed: boolean
  /** When the newest price on the page was taken; null with none. */
  pricedAt: string | null
  groups: { name: string; markets: GameMarket[] }[]
  signals: MarketSignal[]
}

export type PnlRange = '7d' | '28d' | '3m' | '1y' | 'all'

/** One selection of a bet on the P&L screen: the only one of a single, or a leg of an accumulator. */
export interface PnlLeg {
  marketId: string
  event: string
  /** Empty when the selection already names it ("FT O2.5 Goals"). */
  marketType: string
  competition: string | null
  /** As a bet list reads it — see `betSelection`. */
  runner: string
  price: number
  /** Null while its game is still to be settled. */
  result: 'won' | 'lost' | 'void' | null
  /** Final score, home first, when settled from one. */
  score: string | null
}

/** One bet on the P&L screen, open or settled: a single, or an accumulator. */
export interface PnlBet {
  ref: string
  kind: 'single' | 'accumulator'
  /** The bankroll round it was placed in, and counts against. */
  round: number | null
  placedAt: string
  settledAt: string | null
  legs: PnlLeg[]
  /** A single's price; an accumulator's, its legs' prices multiplied. */
  price: number
  stake: number
  status: 'open' | 'settled'
  /** Null while open, and for a settled bet whose result we did not record. */
  result: 'won' | 'lost' | 'void' | null
  /** Null while open. */
  profit: number | null
}

export type PnlShow = 'all' | 'open' | 'settled'

/**
 * GET /api/pnl?range=&round=&show=&limit=. Totals and the chart cover every bet in the
 * range (and round); `bets` is the first `limit` of them that match `show`, newest first.
 */
export interface PnlPayload {
  range: PnlRange
  /** The round filtered on; null for all rounds. */
  round: number | null
  /** The player's rounds, newest first, for the filter. */
  rounds: { number: number; currency: Currency; startedAt: string; endedAt: string | null }[]
  /** How many bets in the range (and round) there are of each kind. */
  counts: { all: number; open: number; settled: number }
  /** More bets match than were sent. */
  more: boolean
  totals: {
    profit: number
    /** Profit as a share of the bankroll it was made from: 10,000 for each round the
     * period's bets were placed in (one round: its 10,000). */
    return: number
    bankroll: number
    /** Everything staked on the bets that profit covers: settled ones, void left out. */
    turnover: number
    won: number
    lost: number
    void: number
    open: number
    /** What the open bets stand to lose: their stakes. */
    exposure: number
  }
  /** Settled profit per day across the range, oldest first, with its running total. */
  series: { date: string; profit: number; cumulative: number }[]
  bets: PnlBet[]
}

export interface MarketListRow {
  marketId: string
  /** The game this row stands for; it opens the game page. */
  eventId: string | null
  event: string
  marketType: string
  competition: string | null
  startTime: string | null
  status: string | null
  inPlay: boolean
  suspended: boolean
  minute: number | null
  score: string | null
  totalMatched: number | null
  /** Narrowest back/lay gap across runners, in ticks. Null when the book is one-sided. */
  spreadTicks: number | null
  /** Strategies that have made a decision here. Empty until one does. */
  strategies: string[]
  /** We hold matched exposure in this market. */
  held: boolean
  workingOrders: number
  /** The biggest live move in this market, or null when nothing is moving. */
  signal: Pick<MarketSignal, 'runner' | 'kind' | 'pct' | 'ticks' | 'windowS'> | null
}

export interface MarketsPayload {
  markets: MarketListRow[]
  /** Every live signal, biggest move first. */
  signals: MarketSignal[]
  dataTier: 'delayed' | 'live'
  lastFrameAgoS: number | null
  /** Age of the newest published book in the list; null when there are none. */
  pricesAgoS: number | null
}

export interface DecisionRow {
  id: number
  ts: string
  strategy: string
  marketId: string
  event: string
  marketType: string
  runner: string | null
  action: string
  side: Side | null
  price: number | null
  size: number | null
  outcome: DecisionOutcome
  /** For a veto: "max liability per market · limit £185.00 · would have been £236.40". */
  detail: string | null
  liability: number | null
}

export interface DecisionCounts {
  total: number
  placed: number
  vetoed: number
  skipped: number
  failed: number
}

export interface DecisionsPayload {
  rows: DecisionRow[]
  counts: DecisionCounts
  shown: number
  lastDecisionAgoS: number | null
  silentStrategies: {
    key: string
    name: string
    status: string
    why: string
    lastDecisionAt: string | null
  }[]
}

export interface GateCheck {
  label: string
  detail: string
  passed: boolean
}

export interface DecisionDetail {
  id: number
  ts: string
  strategy: string
  event: string
  marketType: string
  runner: string | null
  side: Side | null
  price: number | null
  size: number | null
  outcome: DecisionOutcome
  vetoReason: string | null
  signal: Record<string, unknown> | null
  book: { runner: string; backLadder: LadderLevel[]; layLadder: LadderLevel[] } | null
  gates: GateCheck[]
  routedNote: string
}

export interface LimitView {
  name: string
  label: string
  category: string
  unit: 'money' | 'count' | 'ms' | 'seconds' | 'state' | 'price'
  action: BreachAction
  threshold: number | null
  observed: number | null
  /** 0–100. Consumption, not headroom: closer to full is closer to a veto. */
  pct: number
  valueLabel: string
  editable: boolean
}

export interface RiskEventRow {
  id: number
  ts: string
  limitName: string
  action: BreachAction
  threshold: number | null
  observed: number | null
  thresholdLabel: string
  observedLabel: string
  market: string
}

export interface RiskPayload {
  state: TradingState
  stateCause: string | null
  headline: string
  hotLimits: { name: string; pct: number }[]
  summary: {
    dailyLossRemaining: number
    dailyLossUsed: number
    dailyLossLimit: number | null
    dailyLossPct: number
    liability: number
    liabilityLimit: number | null
    liabilityPct: number
    openMarkets: number
    openMarketsLimit: number | null
    openMarketsPct: number
    transactions: number
    transactionsLimit: number | null
    transactionsPct: number
  }
  limits: LimitView[]
  events: RiskEventRow[]
  eventSummary: { total: number; rejects: number; halts: number; flattens: number }
  checksToday: number
  vetoedToday: number
  lastIntentAgoS: number | null
  haltDetail: {
    limitName: string
    action: BreachAction
    threshold: number | null
    observed: number | null
    trippedAt: string
  } | null
}

// ---------------------------------------------------------------------------
// The bet slip
// ---------------------------------------------------------------------------

/** One selection on the slip, as priced now. */
export interface SlipPrice {
  marketId: string
  selectionId: number
  /** The game it belongs to: an accumulator takes one selection per game. */
  eventId: string | null
  event: string
  marketType: string
  runner: string
  /** The bookie price on offer now; null when it cannot be bet on. */
  price: number | null
  /** Why it cannot be bet on, when it cannot. */
  unavailable: string | null
}

/** GET /api/betslip */
export interface BetslipPayload {
  prices: SlipPrice[]
  /** What the current bankroll round can still stake. */
  available: number
  currency: Currency
}

/** POST /api/bets. Every price is the one the player saw; a bet is refused if it has shortened. */
export interface BetsRequest {
  singles: { marketId: string; selectionId: number; price: number; stake: number }[]
  accumulator: { legs: { marketId: string; selectionId: number; price: number }[]; stake: number } | null
}

export interface SlipProblem {
  bet: 'single' | 'accumulator'
  /** The selection it is about; absent when it is about the accumulator as a whole. */
  marketId?: string
  selectionId?: number
  text: string
}

export interface BetsResult {
  ok: boolean
  message: string
  /** Why the slip was refused, per bet. Empty when it was placed. */
  problems: SlipProblem[]
  /** The prices the slip was checked against, to refresh it with. */
  prices: SlipPrice[]
  placed: PlacedBet[]
}

/** One selection of a placed bet, named as bet lists name it (`betSelection`). */
export interface PlacedLeg {
  marketId: string
  selectionId: number
  event: string
  /** Empty when the selection already names it ("FT O2.5 Goals"). */
  marketType: string
  runner: string
  price: number
}

/** A bet as the confirmation shows it: its selections, odds, stake and what it returns. */
export interface PlacedBet {
  ref: string
  bet: 'single' | 'accumulator'
  label: string
  /** A single's price; an accumulator's, its legs' prices multiplied. */
  price: number
  stake: number
  /** Stake × price: what it pays back if it wins. */
  returns: number
  legs: PlacedLeg[]
}

/** GET /api/profile — what Settings shows. */
export interface ProfilePayload {
  email: string
  details: ProfileDetails
  /** True when nothing has been saved yet and the details come from the waitlist or sign-in. */
  prefilled: boolean
  /** The currency the player's next bankroll round starts in. */
  currency: Currency
  /** The current round's currency, which cannot change: its balance is in it. */
  roundCurrency: Currency
}

// ---------------------------------------------------------------------------
// Bankroll rounds
// ---------------------------------------------------------------------------

export const CURRENCIES = ['GBP', 'USD', 'EUR'] as const
export type Currency = (typeof CURRENCIES)[number]

/** Every round starts with this much, in its own currency. */
export const ROUND_BANKROLL = 10_000

/**
 * - `active`: the player's current round, still with money to bet.
 * - `bust`: went to zero — under 1.00 left and nothing open to bring it back.
 * - `settling`: closed by a new round, with bets still to settle into it.
 * - `closed`: closed by a new round, with money left.
 */
export type RoundStatus = 'active' | 'bust' | 'settling' | 'closed'

export interface RoundView {
  number: number
  currency: Currency
  startingBalance: number
  startedAt: string
  endedAt: string | null
  /** Starting balance plus the profit of every settled bet placed in the round. */
  balance: number
  /** What open bets in the round stand to lose. */
  atRisk: number
  /** Balance less what is at risk: the most a new bet can put on the line. */
  available: number
  openBets: number
  settledBets: number
  status: RoundStatus
}

export interface BankrollPayload {
  current: RoundView
  rounds: RoundView[]
  /** The currency set in Settings, which a new round starts in unless another is picked. */
  currency: Currency
}

export interface LeaderboardRow {
  playerId: number
  name: string
  you: boolean
  current: RoundView
  rounds: number
  busts: number
  /** The highest balance any of their rounds has ended on, or stands at, as a share of its start. */
  bestReturn: number
}

export interface PlayerPayload {
  playerId: number
  name: string
  you: boolean
  rounds: RoundView[]
}
